import { useState, memo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DangerCard } from "./DangerCard";
import { useLanguage } from "@/contexts/LanguageContext";

type DeleteConversationsProps = {
  className?: string;
  disabled?: boolean;
  onSuccess?: () => void;
  onDeleteAll?: (password: string) => Promise<void>;
};

export const DeleteConversations = memo(function DeleteConversations({
  className,
  disabled,
  onSuccess,
  onDeleteAll,
}: DeleteConversationsProps) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const [pass, setPass] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!pass || isPending) return;
    setLocalError(null);
    setLocalSuccess(null);
    try {
      setIsPending(true);
      if (onDeleteAll) {
        await onDeleteAll(pass);
      } else {
        // no-op fallback so UI still works in dev
        await new Promise((r) => setTimeout(r, 350));
      }
      setPass("");
      setExpanded(false);
      setLocalSuccess(t("deleteConversationsSuccess"));
      onSuccess?.();
    } catch {
      setLocalError(t("deleteConversationsError"));
    } finally {
      setIsPending(false);
    }
  };

  return (
    <DangerCard
      className={className}
      title={t("deleteConversations")}
      description={t(
        "deleteConversationsDesc"
      )}
      action={
        !expanded ? (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              setLocalSuccess(null);
              setExpanded(true);
            }}
            disabled={disabled || isPending}
          >
            {t("deleteConversations")}
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setExpanded(false);
              setPass("");
              setLocalError(null);
            }}
            disabled={isPending}
          >
            {t("cancel")}
          </Button>
        )
      }
    >
      {expanded && (
        <div className="space-y-2">
          <Label htmlFor="deleteConvPwd">
            {t("passwordToDelete")}
          </Label>
          <Input
            id="deleteConvPwd"
            type="password"
            placeholder="••••••••"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            disabled={isPending}
          />
          <div className="flex gap-2">
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!pass || isPending}
            >
              {isPending ? t("deleting") : t("confirmDelete")}
            </Button>
          </div>
          {localError && <p className="text-sm text-red-600">{localError}</p>}
        </div>
      )}
      {localSuccess && <p className="text-sm text-green-600">{localSuccess}</p>}
    </DangerCard>
  );
});
